// ── AllNotesPage ──
import { useState, useEffect, useMemo } from "react";
import { fmt } from "../../../shared/format";
import { Btn, Icon } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const AllNotesPage = () => {
    const { api, role, view, setModal, delRec } = useApp();
      const [notes, setNotes] = useState([]);
      const [loading, setLoading] = useState(true);
      const [err, setErr] = useState("");
      const [q, setQ] = useState("");
      const [type, setType] = useState("");
      const [page, setPage] = useState(1);
      const pageSize = 25;

      const load = () => {
        setLoading(true); setErr("");
        api.get("getNotes?all=1")
          .then(d => setNotes(Array.isArray(d) ? d : []))
          .catch(() => setErr("Failed to load notes."))
          .finally(() => setLoading(false));
      };
      useEffect(() => { load(); }, []);

      const types = useMemo(
        () => Array.from(new Set(notes.map(n => n.entitytype).filter(Boolean))).sort(),
        [notes]
      );
      const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return notes.filter(n => {
          if (type && n.entitytype !== type) return false;
          if (!term) return true;
          return [n.note, n.source, n.createdby, n.entitytype, n.accountnumber, n.loannumber, n.companyname]
            .some(v => (v || "").toString().toLowerCase().includes(term));
        });
      }, [notes, q, type]);
      useEffect(() => { setPage(1); }, [q, type]);

      const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
      const rows = filtered.slice((page - 1) * pageSize, page * pageSize);
      const fmt = d => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

      const th = { textAlign: "left", padding: "8px 10px", background: "#d4f1f9", color: "#116588", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #b6e2ee", whiteSpace: "nowrap" };
      const td = { padding: "8px 10px", fontSize: 13, color: "#333", borderBottom: "1px solid #eee", verticalAlign: "top" };
      const aBtn = c => ({ background: "none", border: "none", cursor: "pointer", color: c, padding: 4, display: "inline-flex", borderRadius: 4 });
      const eye = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
      const pen = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>;
      const bin = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>;

      return (
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "#116588", borderBottom: "1px solid #e3e8ec", paddingBottom: 6, marginBottom: 16 }}>
            All Notes{!loading && ` (${filtered.length})`}
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search notes, company, source, author…"
              style={{ flex: 1, minWidth: 220, padding: "8px 10px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }} />
            <select value={type} onChange={e => setType(e.target.value)}
              style={{ padding: "8px 10px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}>
              <option value="">All types</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
          {loading ? (
            <div style={{ color: "#888", fontSize: 13, padding: 20 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: "#888", fontSize: 13, padding: 20 }}>No notes found.</div>
          ) : (
            <>
              <div style={{ border: "1px solid #e3e8ec", borderRadius: 6, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 110 }}>Date</th>
                      <th style={{ ...th, width: 150 }}>Type</th>
                      <th style={{ ...th, width: 130 }}>Account Number</th>
                      <th style={{ ...th, width: 120 }}>Loan Number</th>
                      <th style={{ ...th, width: 180 }}>Company Name</th>
                      <th style={th}>Note</th>
                      <th style={{ ...th, width: 160 }}>Created By</th>
                      <th style={{ ...th, width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(n => (
                      <tr key={n.id}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>{fmt(n.createdat || n.notedate)}</td>
                        <td style={td}>
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: "#d4f1f9", color: "#116588", whiteSpace: "nowrap" }}>
                            {n.source || n.entitytype}
                          </span>
                        </td>
                        <td style={td}>{n.accountnumber || "—"}</td>
                        <td style={td}>{n.loannumber || "—"}</td>
                        <td style={td}>{n.companyname || "—"}</td>
                        <td style={td}>{n.note}</td>
                        <td style={{ ...td, whiteSpace: "nowrap", color: "#666" }}>{n.createdby || "—"}</td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <Btn title="View" style={aBtn("#185FA5")} onClick={() => setModal({ type: "note-view", rec: n, onDone: load })}><Icon type="view" /></Btn>
                            {role.canEdit && <Btn title="Edit" style={aBtn("#12b5db")} onClick={() => setModal({ type: "note-edit", rec: n, onDone: load })}><Icon type="edit" /></Btn>}
                            {role.canEdit && <Btn title="Delete" style={aBtn("#993C1D")} onClick={() => delRec("Notes", n.id, "Deleted note", load)} variant="danger"><Icon type="delete" /></Btn>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 12, fontSize: 13 }}>
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: "5px 10px", border: "1px solid #ccc", borderRadius: 4, background: page <= 1 ? "#f5f5f5" : "#fff", cursor: page <= 1 ? "default" : "pointer" }}>Prev</button>
                  <span style={{ color: "#666" }}>Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: "5px 10px", border: "1px solid #ccc", borderRadius: 4, background: page >= totalPages ? "#f5f5f5" : "#fff", cursor: page >= totalPages ? "default" : "pointer" }}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      );
    };
